import ReactPlayer from 'react-player';
import cn from 'classnames';
import { Container } from 'react-scale/components/pages/zeitgeist/layout/container';
import { SplitText } from 'gsap/dist/SplitText';
import { useRef, useState, useEffect, FC } from 'react';
import { gsap } from 'react-scale/lib/gsap';
import { toVw } from 'react-scale/lib/util/math';
import { CTA } from 'react-scale/components/pages/zeitgeist/primitives/cta';
import HubspotForm from 'react-hubspot-form';
import { useMedia } from 'react-use';
import dynamic from 'next/dynamic';

import s from './hero.module.scss';
import { Drawer } from '../../layout/drawer';

import { UseCanvas, ScrollScene } from '@14islands/r3f-scroll-rig';
const ScaleTransmissionMaterial = dynamic(() => import('./3d'), { ssr: false });
import { useAppStore } from '../../context/use-app-store';

const FORM_ID = '32ee0763-f323-441e-8cd6-c2d2cef9d142';

export const Hero: FC<{ form?: boolean }> = ({ form }) => {
  const isLarge = useMedia('(min-width: 1024px)');
  const overtitleRef = useRef<HTMLHeadingElement>();
  const titleRef = useRef<HTMLHeadingElement>();
  const subtitleRef = useRef<HTMLHeadingElement>();
  const formRef = useRef<HTMLDivElement>();
  const tl = useRef<GSAPTimeline>(null);
  const reference3d = useRef<HTMLDivElement>();
  const heroRef = useRef<HTMLDivElement>();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const canvasLoaded = useAppStore(({ canvasLoaded }) => canvasLoaded);

  useEffect(() => {
    if (tl.current || typeof document === 'undefined' || !isLarge || !canvasLoaded) return;

    const entrance = () => {
      const titleSplittedText = new SplitText(titleRef.current, {
        type: 'lines',
        linesClass: s.title__lines
      });
      new SplitText(titleRef.current, { type: 'lines', linesClass: s.lines });
      const subtitleSplittedText = new SplitText(subtitleRef.current, { type: 'lines' });
      new SplitText(subtitleRef.current, {
        type: 'lines',
        linesClass: s.lines
      });
      const overtitleSplittedText = new SplitText(overtitleRef.current, { type: 'chars' });

      tl.current = gsap.timeline({
        paused: true,
        smoothChildTiming: true,
        defaults: {
          duration: 0.7,
          ease: 'power4.out'
        }
      });

      tl.current.fromTo(
        titleSplittedText.lines,
        {
          rotate: 5,
          y: toVw(285)
        },
        {
          duration: 0.95,
          onStart: () => gsap.set(titleRef.current, { visibility: 'visible' }),
          rotate: 0,
          stagger: 0.055,
          y: 0
        }
      );
      tl.current.fromTo(
        subtitleSplittedText.lines,
        {
          rotate: 5,
          y: toVw(85)
        },
        {
          onComplete: () => useAppStore.setState({ timelineCompleted: true }),
          onStart: () => gsap.set(subtitleRef.current, { visibility: 'visible' }),
          rotate: 0,
          stagger: 0.055,
          y: 0
        },
        '<25%'
      );
      tl.current.fromTo(
        overtitleSplittedText.chars,
        { y: toVw(45) },
        {
          onStart: () => gsap.set(overtitleRef.current, { visibility: 'visible' }),
          stagger: 0.04,
          y: 0
        },
        '<10%'
      );
      tl.current.fromTo(
        formRef.current,
        { y: toVw(45) },
        {
          onStart: () => gsap.set(formRef.current, { visibility: 'visible' }),
          y: 0
        },
        '<10%'
      );

      tl.current.timeScale(0.85);
      tl.current.play();
    };

    // If the document is already loaded (maybe the user pressed the
    // browser's back button), we run the entrance animation, otherwise
    // we wait for the load event (we assume it was a full page refresh).
    if (document.readyState === 'complete') {
      setTimeout(entrance, 1000);
    } else {
      window.addEventListener('load', entrance);
    }

    return () => {
      window.removeEventListener('load', entrance);
      tl.current?.kill();
    };
  }, [isLarge, canvasLoaded]);

  useEffect(() => {
    const hubSpotEventHandler = async event => {
      const data = event?.data;

      if (data && data.type === 'hsFormCallback' && data.eventName === 'onFormSubmit') {
        if (data.id === FORM_ID) {
          setFormSubmitted(true);
        }
      }
    };

    window.addEventListener('message', hubSpotEventHandler);

    return () => window.removeEventListener('message', hubSpotEventHandler);
  }, []);

  return (
    <Container className="relative overflow-x-hidden">
      <ReactPlayer
        className="absolute -top-4 -right-44 lg:hidden"
        preload
        url="https://stream.mux.com/8KEXb02giOIanDPAR013O11gs01QPV89Chi.m3u8"
        playsinline
        muted
        volume={0}
        loop
        width={500}
        height={500}
        playing
        controls={false}
      />

      <div className={s.hero} ref={heroRef}>
        <h4 className={s.overtitle} ref={overtitleRef}>
          Zeitgeist
        </h4>
        <h1 className={s.title} ref={titleRef}>
          2023 AI
          <br />
          Readiness Report
        </h1>
        <h2 className={cn(s.subtitle, 'lg:hidden')}>
          At Scale, our mission is to accelerate the development of AI applications to power the
          most ambitious AI projects in the world. That’s why we are excited to introduce the 2nd
          edition of Scale Zeitgeist: AI Readiness&nbsp;Report.
        </h2>
        <h2 className={cn(s.subtitle, !form && 'lg:!mb-0', 'hidden lg:block')} ref={subtitleRef}>
          At Scale, our mission is to accelerate the development of AI applications to power the
          most ambitious AI projects in the world. That’s why we are excited to introduce the 2nd
          edition of Scale Zeitgeist: AI Readiness Report. We are here to get past the hype and show
          you what it&nbsp;really takes to adopt Generative AI.
        </h2>

        <div className={cn(s.form, !form && '!h-auto')} ref={formRef}>
          {form ? (
            <>
              <div
                className={cn(
                  !formSubmitted ? 'opacity-100' : 'opacity-0 absolute w-full',
                  'transition-opacity duration-700 ease-out'
                )}
              >
                <HubspotForm
                  portalId="4823527"
                  formId={FORM_ID}
                  formInstanceId="hero-section-form"
                />
              </div>

              <p
                className={cn(
                  s['submitted-message'],
                  formSubmitted
                    ? 'opacity-100 delay-700'
                    : 'absolute opacity-0 pointer-events-none',
                  'transition-opacity duration-700 ease-out'
                )}
              >
                Thank you for your interest in our 2023 AI Readiness Report. Please check your inbox
                to access the full&nbsp;report.
              </p>
              <CTA
                arrow="right"
                className={cn(
                  s['form__submit'],
                  formSubmitted ? 'absolute opacity-0 pointer-events-none' : 'opacity-100',
                  'transition-opacity duration-700 ease-out'
                )}
                onClick={() =>
                  (
                    document.querySelector(
                      `form#hsForm_${FORM_ID}_hero-section-form > * input.hs-button`
                    ) as HTMLInputElement
                  ).click()
                }
              >
                SUBMIT
              </CTA>
            </>
          ) : (
            <Drawer
              trigger={
                <CTA arrow="bottom" className={s['cta--download']}>
                  Download the full report
                </CTA>
              }
            />
          )}
        </div>
        <div className={s.draw} ref={reference3d} />
      </div>
      {isLarge && (
        <UseCanvas>
          <ScrollScene track={reference3d}>
            {_props => (
              <>
                <mesh>
                  <ScaleTransmissionMaterial />
                </mesh>
              </>
            )}
          </ScrollScene>
        </UseCanvas>
      )}
    </Container>
  );
};
